import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { List, Comment, Button, Notification } from "@arco-design/web-react";
import { IconHeart, IconMessage } from "@arco-design/web-react/icon";

import UpdatePostModal from "../UpdatePostModal";
import DeletePostModal from "../DeletePostModal";
import CommentComponent from "../Comment/Comment";
import CommentInput from "../CommentInput/CommentInput";
import ContentPreview from "./ContentPreview";

import { PostApi, CommentApi } from "../../types/types";
import { handleFeedApiError } from "../../../../../../api/utils/handleFeedApiError";
import { LoggedUser } from "../../../../../../models/Person";
import { deletePostRequest, updatePostRequest } from "../../../../../../api/feed";
import { UserAvatar } from "../../../../../../components/UserAvatar/UserAvatar";
import { getParsedArray } from "../../../../../../utils/getParsedArray";
import { formatDate } from "../../../../../../utils/formatDate";
import { createNewCommentFormData } from "../../../../../../utils/createNewCommentFormData";
import { createUpdatedLikesFormData } from "../../../../../../utils/createUpdatedLikesFormData";

import './Post.css';
import { PostAttachments } from "../PostAttachments/PostAttachments";


interface IPostProps {
  post: PostApi;
  currentUser: LoggedUser;
  refetch: () => void;
}

const Post = ({
  post,
  currentUser,
  refetch,
}: IPostProps) => {
  const { t } = useTranslation();

  const {
    id: post_id,
    user_id,
    likes,
    comments,
    content,
    heading,
    created_at,
    updated_at,
  } = post;

  const [isMutating, setIsMutating] = useState<boolean>(false);

  const [expandedPost, setExpandedPost] = useState<number | null>(null);

  const parsedLikes = getParsedArray(likes);
  const parsedComments = getParsedArray(comments);

  const handleExpandComments = (postId: number) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  const { mutateAsync: updatePost, isPending: isPostUpdating } = useMutation({
    mutationFn: updatePostRequest,
    onSuccess: () => {
      refetch();
      Notification.success({
        content: t('notification_messages.post_updated_success'),
      });
    },
    onError: () => {
      Notification.error({
        content: t('notification_messages.post_updated_error'),
      });
    },
  });

  const { mutateAsync: updateComment } = useMutation({
    mutationFn: updatePostRequest,
    onSuccess: () => {
      refetch();
      Notification.success({
        content: t('notification_messages.comment_updated_success'),
      });
    },
    onError: () => {
      Notification.error({
        content: t('notification_messages.comment_updated_error'),
      });
    },
  });

  const { mutateAsync: deletePost } = useMutation({
    mutationFn: deletePostRequest,
    onSuccess: () => {
      refetch();
      Notification.success({
        content: t('notification_messages.post_deleted_success'),
      });
    },
    onError: () => {
      Notification.error({
        content: t('notification_messages.post_deleted_error'),
      });
    },
  });

  const { mutateAsync: likePost } = useMutation({
    mutationFn: updatePostRequest,
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      Notification.error({
        content: t('notification_messages.post_liked_error'),
      });
    },
  });

  const handleUpdateComment = async (postId: number, updatedPost: FormData) => {
    try {
      await updateComment([postId, updatedPost]);
    } catch (error) {
      handleFeedApiError(error, t);
    }
  };

  const handleUpdatePost = async (postId: number, updatedPost: FormData) => {
    try {
      await updatePost([postId, updatedPost]);
    } catch (error) {
      handleFeedApiError(error, t);
    }
  };

  const handleDeletePost = (postId: number) => {
    deletePost(postId);
  };

  const handleLike = (post: PostApi, userId: number) => {
    const formData = createUpdatedLikesFormData({ userId, post })

    likePost([post.id, formData]);
  };

  const { mutateAsync: addComment } = useMutation({
    mutationFn: updatePostRequest,
    onSuccess: () => {
      Notification.success({
        content: t('notification_messages.comment_added_success'),
      });
      refetch();
      setIsMutating(false);
    },
    onError: () => {
      Notification.error({
        content: t('notification_messages.comment_added_error'),
      });
      setIsMutating(false);
    },
  });

  const handleAddComment = (post: PostApi, newComment: string) => {
    if (isMutating || newComment.trim() === "") return;

    setIsMutating(true);

    if (post.id) {
      const formData = createNewCommentFormData({ user: currentUser, post, content: newComment });
      addComment([post.id, formData])
        .finally(() => {
          setIsMutating(false);
        });
    }
  };

  return (
    <List.Item className="post">
      <div className="post-header">
        <div style={{ marginRight: 10 }}>
          <UserAvatar
            avatarUrl={post.avatar_url}
            fullName={post.user_name}
            size={36}
            shape="circle"
            alt={post.user_name}
          />
        </div>
        <div className="post-header__content-wrapper">
          <div className="post-header__content">
            <b className="post-title">
              {heading}
            </b>
            <span className="post-author-name">
              {post.user_name}
            </span>
            <span className="post-date">
              {formatDate(created_at)}{" "}
              {created_at !== updated_at &&
                ` | ${t('home_page.edited')} - ${formatDate(updated_at)}`}
            </span>
          </div>
          <div className="post-header__actions">{(user_id === currentUser?.id || currentUser.isAdmin)
            && (<><UpdatePostModal onUpdatePost={handleUpdatePost} isPostUpdating={isPostUpdating} post={post} />
              <DeletePostModal postId={post_id} onDeletePost={handleDeletePost} /></>)}
          </div>
        </div>
      </div>
      <div className="post-content__wrapper">
        <ContentPreview content={content} />
        <PostAttachments
          document_url={post.document_url}
          file_name={post.file_name}
          images={post.images.split(';;;')}
        />
      </div>
      <div key={post_id} className="post-content-actions__wrapper">
        <Button className="post-content-actions__like"
          onClick={() => handleLike(post, currentUser.id)}
        >
          <IconHeart
            style={{
              fill: parsedLikes?.includes(currentUser.id)
                ? "red"
                : "transparent",
            }}
          />
          {parsedLikes.length || ""}
        </Button>
        <Button
          onClick={() => post_id && handleExpandComments(post_id)}
          size="small"
          className="post-content-actions__comment"
        >
          <IconMessage />
          <span className="comment-button-text">
            {!parsedComments.length && t('home_page.add_comment_button')}
            {!!parsedComments.length && (
              expandedPost === post_id
                ? t('home_page.hide_comments_button') : t('home_page.show_comments_button', { number: parsedComments.length }))
            }
          </span>
        </Button>
      </div>
      {expandedPost === post_id && (
        <div className="comments-wrapper">
          <ul className="comments-list">
            {parsedComments &&
              parsedComments.map((comment: CommentApi) => (
                <CommentComponent
                  key={comment.comment_id}
                  comment={comment}
                  post={post}
                  currentUserId={currentUser.id}
                  isCurrentUserAdmin={currentUser.isAdmin}

                  isMutating={isMutating}
                  setIsMutating={setIsMutating}
                  refetch={refetch}
                  onUpdateComment={handleUpdateComment}
                />
              ))}
          </ul>
          <CommentInput
            post={post}
            isMutating={isMutating}
            onAddComment={handleAddComment}
          />
        </div>
      )}
    </List.Item>
  );
};

export default Post;